import type {
  Guest,
  Reservation,
  ReservationItem,
  ReservationOwner,
  SailPackage,
} from '@/root/shared-types/reservation/reservation-types'
import { isTicketItem } from '@/root/shared-helpers/reservation/reservation-item-filters'

const createItemSnapshot = (items: ReservationItem[] | undefined) =>
  (items ?? [])
    .filter(isTicketItem)
    .map((item) =>
      JSON.stringify({
        ownerSeqNs: [...(item.ownerSeqNs ?? [])].toSorted((left, right) => left - right),
        priceCategory: item.priceCategory,
        quantity: item.quantity,
        resident: item.resident,
        sailPackageSeqN: item.sailPackageSeqN ?? 1,
        type: item.type,
      })
    )
    .toSorted()
    .join('|')

const createSailPackageSnapshot = (sailPackages: SailPackage[] | undefined) =>
  (sailPackages ?? [])
    .map((sailPackage) =>
      JSON.stringify({
        code: sailPackage.code,
        sailRefs: sailPackage.sailRefs
          .map(({ departureDate, departureTime, routeLeg, sailRefId }) => ({
            departureDate,
            departureTime,
            routeLeg,
            sailRefId,
          }))
          .toSorted((left, right) => left.sailRefId - right.sailRefId),
        seqN: sailPackage.seqN ?? 1,
      })
    )
    .toSorted()
    .join('|')

export function hasSailPackageChanged(
  currentSailPackage: SailPackage | undefined,
  committedSailPackages: SailPackage[] | undefined
): boolean {
  if (!currentSailPackage) return false

  const committedSailPackage = committedSailPackages?.find(({ seqN }) => seqN === currentSailPackage.seqN)

  return (
    createSailPackageSnapshot([currentSailPackage]) !==
    createSailPackageSnapshot(committedSailPackage ? [committedSailPackage] : undefined)
  )
}

const createOwnerSnapshot = (owner: ReservationOwner | undefined) =>
  JSON.stringify({
    email: owner?.email ?? null,
    firstName: owner?.firstName ?? null,
    lastName: owner?.lastName ?? null,
    phoneNumber: owner?.phoneNumber ?? null,
    phones: (owner?.phones ?? [])
      .map(({ intlCode, phoneNumber, phoneNumberSuffix, primary }) => ({
        intlCode,
        phoneNumber,
        phoneNumberSuffix: phoneNumberSuffix ?? null,
        primary: primary === true,
      }))
      .toSorted((left, right) =>
        `${left.intlCode}-${left.phoneNumber}`.localeCompare(`${right.intlCode}-${right.phoneNumber}`)
      ),
  })

export function hasTicketQuantityChanged(
  currentItems: ReservationItem[] | undefined,
  originalItems: ReservationItem[] | undefined
): boolean {
  const buildQtyMap = (items: ReservationItem[] | undefined) => {
    const map = new Map<string, number>()
    ;(items ?? []).filter(isTicketItem).forEach((item) => {
      const key = `${item.sailPackageSeqN ?? 1}-${item.priceCategory}`
      map.set(key, (map.get(key) ?? 0) + item.quantity)
    })
    return map
  }
  const current = buildQtyMap(currentItems)
  const original = buildQtyMap(originalItems)
  const keys = new Set([...current.keys(), ...original.keys()])
  for (const key of keys) {
    if ((current.get(key) ?? 0) !== (original.get(key) ?? 0)) return true
  }
  return false
}

export function hasGuestDataChanged(currentGuests: Guest[] | undefined, originalGuests: Guest[] | undefined): boolean {
  const current = currentGuests ?? []
  const original = originalGuests ?? []
  if (current.length !== original.length) return true
  const origMap = new Map(original.map((g) => [g.seqN, g]))
  for (const guest of current) {
    const orig = origMap.get(guest.seqN)
    if (!orig) return true
    if (
      guest.firstName !== orig.firstName ||
      guest.lastName !== orig.lastName ||
      guest.birthday !== orig.birthday ||
      guest.citizenship !== orig.citizenship ||
      guest.personalIdentificationNumber !== orig.personalIdentificationNumber ||
      guest.gender !== orig.gender ||
      guest.comments !== orig.comments
    )
      return true
  }
  return false
}

export function hasVehicleDataChanged(current: Reservation | undefined, original: Reservation | undefined): boolean {
  const snapshot = (reservation: Reservation | undefined) =>
    (reservation?.items ?? [])
      .filter((item) => item.type === 'VEHICLE')
      .flatMap((item) =>
        (item.ownerSeqNs ?? []).map((ownerSeqN) => {
          const vehicle = (reservation?.vehicles ?? []).find((v) => v.seqN === ownerSeqN)
          return JSON.stringify({
            sailPackageSeqN: item.sailPackageSeqN ?? 1,
            priceCategory: item.priceCategory,
            licencePlate: vehicle?.licencePlate ?? null,
            type: vehicle?.type ?? null,
            widthInCm: vehicle?.widthInCm ?? null,
            heightInCm: vehicle?.heightInCm ?? null,
            lengthInCm: vehicle?.lengthInCm ?? null,
            weightInKg: vehicle?.weightInKg ?? null,
            weightOnSails: (vehicle?.weightOnSails ?? []).toSorted(
              (left, right) => left.sailRefId - right.sailRefId || left.weightInKg - right.weightInKg
            ),
            companyRegistrationNumber: vehicle?.companyRegistrationNumber ?? null,
            handicapped: vehicle?.handicapped === true,
          })
        })
      )
      .toSorted()
      .join('|')
  return snapshot(current) !== snapshot(original)
}

export function hasReservationChanged(current: Reservation, original: Reservation): boolean {
  return (
    createItemSnapshot(current.items) !== createItemSnapshot(original.items) ||
    hasGuestDataChanged(current.guests, original.guests) ||
    hasVehicleDataChanged(current, original) ||
    createSailPackageSnapshot(current.sailPackages) !== createSailPackageSnapshot(original.sailPackages) ||
    createOwnerSnapshot(current.reservationOwner) !== createOwnerSnapshot(original.reservationOwner)
  )
}
